import React, { useState, useEffect } from 'react';
import '../styles/YouTubeDownloaderPanel.css';

const COBALT_ENDPOINTS = [
    'https://api.qwkuns.me/',
    'https://cobalt.api.sciter.io/',
    'https://co.wuk.sh/api/json',
    'https://cobalt.stream/api/json'
];

const PIPED_INSTANCES = [
    'https://pipedapi.kavin.rocks/streams/',
    'https://api.piped.privacydev.net/streams/',
    'https://pipedapi.mha.fi/streams/'
];

const INVIDIOUS_INSTANCES = [
    'https://vid.puffyan.us/api/v1/videos/',
    'https://invidious.nerdvpn.de/api/v1/videos/'
];

const YouTubeDownloaderPanel = () => {
    const [url, setUrl] = useState('');
    const [quality, setQuality] = useState('720');
    const [loading, setLoading] = useState(false);
    const [videoData, setVideoData] = useState(null);
    const [error, setError] = useState(null);
    const [history, setHistory] = useState([]);

    useEffect(() => {
        const savedHistory = localStorage.getItem('vortex_yt_downloader_history');
        if (savedHistory) {
            try { setHistory(JSON.parse(savedHistory)); } catch (e) { console.error(e); }
        }
    }, []);

    const saveToHistory = (item) => {
        const updated = [item, ...history.filter(h => h.url !== item.url)].slice(0, 10);
        setHistory(updated);
        localStorage.setItem('vortex_yt_downloader_history', JSON.stringify(updated));
    };

    const extractVideoId = (url) => {
        const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
        const match = url.match(regExp);
        return (match && match[7].length === 11) ? match[7] : false;
    };

    const handleAnalyze = async (vCodec = 'h264', downloadMode = 'auto') => {
        if (!url) {
            setError('Por favor ingresa una URL de YouTube o red social.');
            return;
        }

        setError(null);
        setLoading(true);
        setVideoData(null);

        let success = false;
        let lastErrorMsg = '';

        // 1. Intento con clúster Cobalt
        for (const apiUrl of COBALT_ENDPOINTS) {
            try {
                const response = await fetch(apiUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    body: JSON.stringify({
                        url: url,
                        videoQuality: quality,
                        youtubeVideoCodec: vCodec === 'h264' ? 'h264' : 'av1',
                        downloadMode: downloadMode === 'audio' ? 'audio' : 'auto'
                    })
                });

                if (!response.ok) continue;

                const data = await response.json();
                console.log(`Cobalt Response (${apiUrl}):`, data);

                if (data.status === 'error' || data.error) {
                    lastErrorMsg = data.text || (typeof data.error === 'string' ? data.error : JSON.stringify(data.error));
                    continue;
                }

                if (data.url || data.status === 'redirect' || data.status === 'stream') {
                    const videoId = extractVideoId(url);
                    const thumbnail = videoId
                        ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
                        : 'https://placehold.co/400x225?text=Video+Procesado';
                    const maxResThumbnail = videoId
                        ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
                        : null;

                    const newVideoData = {
                        title: "Media Analizado Correctamente (Cobalt Engine) ✨",
                        videoId,
                        thumbnail,
                        maxResThumbnail,
                        downloadUrl: data.url,
                        status: data.status,
                        mode: downloadMode,
                        quality: downloadMode === 'audio' ? 'Audio MP3' : `${quality}p`,
                        originalUrl: url
                    };

                    setVideoData(newVideoData);
                    saveToHistory({ url, title: `Video (${newVideoData.quality})`, thumbnail, timestamp: new Date().toLocaleDateString() });
                    success = true;
                    break;
                }
            } catch (err) {
                lastErrorMsg = err.message;
            }
        }

        // 2. Intento con Piped API si falló Cobalt y es un enlace de YouTube
        const videoId = extractVideoId(url);
        if (!success && videoId) {
            console.log("Activando Motor Secundario (Piped API)...");
            for (const pipedUrl of PIPED_INSTANCES) {
                try {
                    const res = await fetch(`${pipedUrl}${videoId}`);
                    if (!res.ok) continue;
                    const pipedData = await res.json();

                    if (pipedData && (pipedData.videoStreams || pipedData.audioStreams)) {
                        let selectedStream = null;
                        if (downloadMode === 'audio') {
                            selectedStream = pipedData.audioStreams?.[0]?.url;
                        } else {
                            // Buscar flujo de video con audio o mejor resolución
                            const stream = pipedData.videoStreams?.find(v => v.quality.includes(quality)) || pipedData.videoStreams?.[0];
                            selectedStream = stream?.url;
                        }

                        if (selectedStream) {
                            const newVideoData = {
                                title: pipedData.title || "Video Extraído (Piped Engine) ✨",
                                videoId,
                                thumbnail: pipedData.thumbnailUrl || `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
                                maxResThumbnail: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
                                downloadUrl: selectedStream,
                                status: 'stream',
                                mode: downloadMode,
                                quality: downloadMode === 'audio' ? 'Audio MP3' : `${quality}p`,
                                originalUrl: url
                            };

                            setVideoData(newVideoData);
                            saveToHistory({ url, title: newVideoData.title, thumbnail: newVideoData.thumbnail, timestamp: new Date().toLocaleDateString() });
                            success = true;
                            break;
                        }
                    }
                } catch (e) {
                    console.error("Error Piped:", e);
                }
            }
        }

        // 3. Si fallaron los motores automáticos, mostrar alternativa de descarga directa inteligente
        if (!success) {
            const fallbackLink = videoId ? `https://ssyoutube.com/watch?v=${videoId}` : url;
            setError(`⚠️ Los servidores directos están saturados o requieren solución de captcha. Puedes descargar directamente haciendo clic abajo.`);
            if (videoId) {
                setVideoData({
                    title: "Acceso Directo Espejo (Servidor Alternativo)",
                    videoId,
                    thumbnail: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
                    maxResThumbnail: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
                    downloadUrl: fallbackLink,
                    isExternalFallback: true,
                    originalUrl: url
                });
            }
        }
        setLoading(false);
    };

    return (
        <div className="yt-downloader-container">
            <div className="yt-downloader-header">
                <h2>🎬 YouTube & Media Downloader Pro (Vortex Cobalt Engine)</h2>
                <p>Conversión directa en alta definición, extracción de audio y descarga de miniaturas HD.</p>
            </div>

            <div className="search-section" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <input
                    type="text"
                    className="yt-input"
                    placeholder="Pega el enlace de YouTube, Instagram, TikTok o Facebook..."
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAnalyze()}
                />

                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <label style={{ fontSize: '0.85rem', color: 'var(--gold-light)', fontWeight: 'bold' }}>
                        Calidad deseada:
                    </label>
                    <select
                        value={quality}
                        onChange={(e) => setQuality(e.target.value)}
                        style={{
                            background: '#0a1628',
                            color: '#fff',
                            border: '1px solid #2a3a5c',
                            borderRadius: '8px',
                            padding: '8px 12px',
                            fontSize: '0.85rem',
                            outline: 'none'
                        }}
                    >
                        <option value="1080">1080p Full HD</option>
                        <option value="720">720p HD</option>
                        <option value="480">480p SD</option>
                        <option value="360">360p Móvil</option>
                    </select>

                    <div className="action-buttons" style={{ display: 'flex', gap: '10px', marginLeft: 'auto' }}>
                        <button
                            className="analyze-btn"
                            onClick={() => handleAnalyze('h264', 'auto')}
                            disabled={loading}
                        >
                            {loading ? <div className="loader"></div> : '🎥 Descargar Video'}
                        </button>
                        <button
                            className="analyze-btn"
                            style={{ background: '#10b981' }}
                            onClick={() => handleAnalyze('h264', 'audio')}
                            disabled={loading}
                        >
                            {loading ? <div className="loader"></div> : '🎵 Solo Audio (MP3)'}
                        </button>
                    </div>
                </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            {videoData && (
                <div className="results-section" style={{ marginTop: '20px' }}>
                    <div className="video-info">
                        <div className="thumbnail-wrapper" style={{ position: 'relative' }}>
                            <img src={videoData.thumbnail} alt="Thumbnail" />
                            {videoData.videoId && (
                                <div style={{ marginTop: '10px' }}>
                                    <iframe
                                        title="Preview"
                                        src={`https://www.youtube.com/embed/${videoData.videoId}`}
                                        style={{ width: '100%', height: '180px', borderRadius: '8px', border: 'none' }}
                                        allowFullScreen
                                    />
                                </div>
                            )}
                        </div>
                        <div className="video-details">
                            <h3>{videoData.title}</h3>
                            <div className="video-meta">
                                <span>Estado: ✨ Listo para descargar</span>
                                <span>Calidad: {videoData.quality}</span>
                                <span>Modo: {videoData.mode === 'audio' ? 'Solo Audio' : 'Video + Audio'}</span>
                            </div>

                            <div style={{ marginTop: '1.5rem', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                <a
                                    href={videoData.downloadUrl}
                                    className="download-link"
                                    style={{ padding: '0.8rem 2rem', fontSize: '1rem', background: '#ef4444', color: '#fff', borderRadius: '8px', textDecoration: 'none', fontWeight: 'bold' }}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    🚀 DESCARGAR MEDIA
                                </a>

                                {videoData.maxResThumbnail && (
                                    <a
                                        href={videoData.maxResThumbnail}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ padding: '0.8rem 1.5rem', fontSize: '0.9rem', background: 'rgba(197, 160, 89, 0.2)', border: '1px solid var(--gold-matte)', color: 'var(--gold-light)', borderRadius: '8px', textDecoration: 'none', fontWeight: 'bold' }}
                                    >
                                        🖼️ Miniatura HD
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {history.length > 0 && (
                <div style={{ marginTop: '2.5rem', background: 'rgba(15, 25, 35, 0.6)', padding: '16px', borderRadius: '12px', border: '1px solid #1e293b' }}>
                    <h4 style={{ margin: '0 0 12px 0', color: 'var(--gold-matte)', fontSize: '0.9rem' }}>
                        📜 Historial de Análisis Recientes
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {history.map((h, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.8rem', padding: '6px 10px', background: 'rgba(255,255,255,0.02)', borderRadius: '6px' }}>
                                <span style={{ color: '#e2e8f0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '300px' }}>{h.url}</span>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <span style={{ color: '#64748b', fontSize: '0.75rem' }}>{h.timestamp}</span>
                                    <button
                                        onClick={() => { setUrl(h.url); handleAnalyze(); }}
                                        style={{ background: 'rgba(197, 160, 89, 0.2)', border: 'none', color: 'var(--gold-light)', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }}
                                    >
                                        Re-analizar
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="support-links-section" style={{ marginTop: '3rem', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '2rem' }}>
                <h4 style={{ textAlign: 'center', marginBottom: '1.5rem', opacity: 0.9 }}>🌐 Fuentes de Contenido Soportadas</h4>
                <div className="support-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem' }}>
                    <a href="https://www.youtube.com" target="_blank" rel="noopener noreferrer" className="support-link-card" title="YouTube">YouTube</a>
                    <a href="https://www.instagram.com" target="_blank" rel="noopener noreferrer" className="support-link-card" title="Instagram">Instagram</a>
                    <a href="https://www.tiktok.com" target="_blank" rel="noopener noreferrer" className="support-link-card" title="TikTok">TikTok</a>
                    <a href="https://x.com" target="_blank" rel="noopener noreferrer" className="support-link-card" title="X (Twitter)">X / Twitter</a>
                    <a href="https://www.facebook.com" target="_blank" rel="noopener noreferrer" className="support-link-card" title="Facebook">Facebook</a>
                </div>
            </div>
        </div>
    );
};

export default YouTubeDownloaderPanel;
